import React from 'react';
import PropTypes from 'prop-types';
import COLORS from 'constants/colors';
import styled from 'styled-components';
import { Row, Col, Text, Separator } from '@cuemath/comps';
import { Checklist } from 'styled-icons/octicons/Checklist';

import Options from './options';

const PRODUCT_TYPE = {
  CODING: 'CODING',
};

const REMARK_COLOR = {
  HIGH: '#00CA8D',
  MEDIUM: '#FFB100',
  LOW: '#FF6782',
};

const REMARK = {
  HIGH: 'HIGH',
  MEDIUM: 'MEDIUM',
  LOW: 'NEEDS SUPPORT',
};

const StyledText = styled(Text)`
  margin-left: 18px;
`;

const SubHeader = styled(Text)`
  font-weight: 600;
`;

const StyledIcon = styled(Checklist)`
  color: ${COLORS.COLA};
  margin-right: 12px;
`;

const LinkNav = styled.a`
  cursor: pointer;
  color: ${COLORS.SKY};
  margin: auto 8px;
  font-size: 12px;
  &:hover {
    color: ${COLORS.SKY};
  }
  &:active {
    color: ${COLORS.SKY};
  }
`;

const getOrigin = () => {
  if (process.env.NODE_ENV === 'development') {
    return 'https://local.cuemath.com';
  }

  if (process.env.REACT_APP_ENV === 'staging') {
    return 'https://prod.leaptest.cuemath.com';
  }

  return 'https://leap.cuemath.com';
};

const LabelValue = ({ label, value }) => (
  <Row alignItems="center" gap={4}>
    <Text color={COLORS.COLA}>{label}: &nbsp;</Text>
    <Text color={COLORS.COLA}>{value}</Text>
  </Row>
);

LabelValue.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
};

LabelValue.defaultProps = {
  value: null,
};

const DemoDetails = ({
  attemptAnalysis,
  config,
  stdName,
  teacherFeedback,
  studentClassroomId,
  productCode,
}) => {
  const {
    worksheets = [],
    total_correct_answers: totalCorrectAnswers,
    attempted_questions: attemptedQuestions,
    accuracy,
  } = attemptAnalysis || {};
  const { answers = [], id: courseType } = teacherFeedback || {};
  const { feedback = [] } = config || {};
  const [courseFeedback] = feedback.filter(({ id }) => id === courseType) || [];
  const { form } = courseFeedback || {};

  let reportConfig = [];

  if (form && Array.isArray(form) && form.length > 1) {
    const { questions } = form[1];

    reportConfig = questions;
  }

  const isNotCoding = productCode !== PRODUCT_TYPE.CODING;

  return (
    <div>
      <Row alignItems="center" nowrap>
        <StyledIcon size={36} />
        <Text size="larger" color={COLORS.COLA}>
          Demo Details
        </Text>
      </Row>
      <Separator height={16} transparent />
      <Row>
        <Col sm={7} xs={12} gutter={0} gap={8}>
          <SubHeader color={COLORS.COLA}>Worksheet(s) opened:</SubHeader>
          <Separator height={4} transparent />
          {worksheets.length === 0 && <StyledText color={COLORS.COLA}>None</StyledText>}
          {worksheets.map((chapter, idx) => (
            <Options key={`${chapter}-${idx}`} value={chapter} />
          ))}
          <Separator height={8} transparent />
          {isNotCoding && (
            <div>
              <LabelValue
                label="Number of questions attempted"
                value={`${attemptedQuestions}`}
              />
              <LabelValue
                label="Number of questions correct"
                value={`${totalCorrectAnswers}`}
              />
              <LabelValue
                label="Accuracy"
                value={accuracy ? `${accuracy.toFixed(2)}%` : '-'}
              />
            </div>
          )}
        </Col>
        <Col sm={5} xs={12} gutter={0} gap={8}>
          <SubHeader color={COLORS.COLA}>
            Demo Report:
            {answers.length > 0 && (
              <LinkNav
                target="_blank"
                href={`${getOrigin()}/demo-report/${studentClassroomId}${
                  stdName ? `?name=${stdName}` : ``
                }`}
              >
                (click to view full report)
              </LinkNav>
            )}
          </SubHeader>
          {answers.map(({ id, value }) => {
            const [questionConfig] =
              reportConfig.filter(({ id: questionId }) => id === questionId) || [];
            const { title } = questionConfig || {};
            const valueUpperCase = value.toUpperCase();

            return (
              <Row alignItems="center" gap={4} key={id}>
                <Col sm={7} xs={7} gutter={0}>
                  <Text color={COLORS.COLA}>{title}: &nbsp;</Text>
                </Col>
                <Col sm={5} xs={5} gutter={4}>
                  <Text color={REMARK_COLOR[valueUpperCase]} weight="semibold">
                    {REMARK[valueUpperCase]}
                  </Text>
                </Col>
              </Row>
            );
          })}
        </Col>
      </Row>
    </div>
  );
};

DemoDetails.propTypes = {
  attemptAnalysis: PropTypes.shape({}),
  config: PropTypes.shape({}),
  stdName: PropTypes.string,
  studentClassroomId: PropTypes.string.isRequired,
  teacherFeedback: PropTypes.shape({}),
  productCode: PropTypes.string,
};

DemoDetails.defaultProps = {
  attemptAnalysis: null,
  config: null,
  stdName: null,
  teacherFeedback: null,
  productCode: null,
};

export default DemoDetails;
